import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  ListItem,
  Skeleton,
  Spacer,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { getAsset } from '@pelicargo/tenants';
import { QuoteStatus, RequestStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { QuoteStatusConfirmedLike } from '@pelicargo/utils';
import { memo, useEffect, useMemo } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { HiArrowRight } from 'react-icons/hi';
import { HiLink } from 'react-icons/hi2';
import { useInView } from 'react-intersection-observer';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

import { trpc } from '../config/trpc';
import { UseQuoteCounts, useQuoteCounts } from '../hooks/quote/useQuoteCounts';
import { useWatchers } from '../hooks/requestWatcher/useWatchers';
import { SearchRequestResponse } from '../hooks/useSearchRequests';
import { isRequestActive } from './RequestList';
import { RequestStatusTag } from './RequestStatusTag';

type Request = SearchRequestResponse['results']['requests'][0];

type Params = {
  requestId: string;
};

interface Props {
  previousRequestId: string;
  nextRequestId: string;
  initialRequest?: Request;
  onInView: () => void;
  updateRequest: SearchRequestResponse['updateRequest'];
  hideInactive?: boolean;
  quoteCounts?: UseQuoteCounts[0];
  /** Ingested emails awaiting review on this request. A number, not the lookup
   *  set it comes from, so the memoised row does not re-render on every fetch. */
  pendingDraftCount?: number;
}

const RequestListItemBase = memo(
  ({
    shouldHideBorder,
    initialRequest,
    onInView,
    updateRequest,
    hideInactive,
    quoteCounts,
    pendingDraftCount = 0,
    isSelected,
  }: Props & {
    isSelected: boolean;
    shouldHideBorder: boolean;
  }) => {
    const [ref, inView] = useInView();
    // The search cache is the source of truth. Live updates are merged back
    // into it (see updateRequest) so the row keeps the latest value even after
    // the panel unmounts/remounts on a tab switch, instead of reverting to the
    // stale value it was first seeded with.
    const request = initialRequest;
    const isActive = request ? isRequestActive(request) : true;

    trpc.onRequestUpdated.useSubscription(
      { id: Number(initialRequest?.id) },
      {
        onData: (newData) => {
          // iRequest carries the raw account shape from the server; the cache
          // merge only reads the fields shared with the search document.
          updateRequest(initialRequest.id, newData.iRequest as Partial<Request>);
        },
        onError: (err: any) => {
          console.error(err);
        },
      },
    );

    useEffect(() => {
      if (inView) onInView?.();
    }, [inView, onInView]);

    const from = request?.origins?.join(', ');
    const to = request?.destinations?.join(', ');

    // Since it's used twice and is extremely ugly, just putting it here
    const watchers = useWatchers(request.id);

    const isLoaded = !!request;

    const pendingQuoteCount = useMemo(() => {
      const pendingCount = quoteCounts?.[QuoteStatus.Pending]?.length || 0;
      const readyForQCCount = quoteCounts?.[QuoteStatus.ReadyForQC]?.length || 0;

      return pendingCount + readyForQCCount;
    }, [quoteCounts]);

    const waitingQuoteCount = useMemo(() => quoteCounts?.[QuoteStatus.SentToCarrier]?.length || 0, [quoteCounts]);

    const completedQuoteCount = useMemo(() => {
      const bookedCount = QuoteStatusConfirmedLike.reduce((agg, k) => agg + (quoteCounts?.[k]?.length || 0), 0);
      const quoteInProgressCount = quoteCounts?.[QuoteStatus.InProgress]?.length || 0;
      return bookedCount + quoteInProgressCount;
    }, [quoteCounts]);

    const watcherNames = useMemo(() => {
      return watchers.join(', ');
    }, [watchers]);

    const linkIcon = useMemo(() => {
      if (request?.main_request_id) {
        return <Icon as={HiLink} mr="2" />;
      }
      return null;
    }, [request?.main_request_id]);

    if (!isActive && hideInactive) return null;

    const today = new Date();
    const highlightUntil =
      typeof request?.account?.highlight_until === 'number'
        ? new Date(request?.account?.highlight_until * 1000)
        : new Date(request?.account?.highlight_until);

    return (
      <ListItem
        id={`requestListItem-${request?.id}`}
        key={initialRequest.id}
        ref={ref}
        as={NavLink}
        to={`requests/${request?.id}/airline`}
        state={{ request }}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="flex-start"
        p="2"
        w="full"
        borderTopWidth="0.5px"
        borderBottomWidth="0.5px"
        borderColor={shouldHideBorder ? 'transparent' : 'border.default'}
        borderRadius={isSelected ? 'md' : 'none'}
        transition="all 200ms ease"
        background={isSelected ? 'bg.muted' : 'bg.surface'}
        _hover={{
          backgroundColor: 'bg.muted',
          borderRadius: 'md',
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          w="full"
          pointerEvents="none"
          spacing={0}
        >
          <Box w="full" borderRightWidth={1} pr={2}>
            <Stack direction="row" justify="space-between" mb={1}>
              <HStack spacing={1}>
                <RequestStatusTag status={request?.request_status as RequestStatus} isLoaded={isLoaded} />
                {/* An ingested email is waiting on this request — the same
                    yellow the request-page banner uses, so the colour reads as
                    "AI needs you" in both places. */}
                <Show if={pendingDraftCount > 0}>
                  <Tooltip label={`${pendingDraftCount} email awaiting review`}>
                    <Badge
                      colorScheme="orange"
                      variant="solid"
                      fontSize="xxs"
                      textTransform="none"
                      pointerEvents="initial"
                    >
                      🤖 {pendingDraftCount}
                    </Badge>
                  </Tooltip>
                </Show>
              </HStack>
              <Tooltip label={watcherNames}>
                <AvatarGroup size="xs" spacing={-2} max={3}>
                  {watchers.map((val) => (
                    <Avatar name={val} fontSize="xxs" title={val} key={val} pointerEvents="initial" />
                  ))}
                </AvatarGroup>
              </Tooltip>
            </Stack>
            <HStack spacing={2} align="top">
              <Skeleton isLoaded={isLoaded}>
                <Flex align="center">
                  <Show if={!!linkIcon}>{linkIcon}</Show>
                  <Text textStyle="bodySmallSemibold">R{request?.id}</Text>
                </Flex>
              </Skeleton>
              <HStack spacing={1} align="center">
                <Skeleton isLoaded={isLoaded}>
                  <Text textStyle="bodySmallRegular">{from}</Text>
                </Skeleton>
                <Icon as={HiArrowRight} />
                <Skeleton isLoaded={isLoaded}>
                  <Text textStyle="bodySmallRegular">{to}</Text>
                </Skeleton>
              </HStack>
            </HStack>
            <Stack direction="row" justify="space-between" w="full" mt="1">
              <Skeleton isLoaded={isLoaded} w="full">
                <Stack direction="row" spacing="1" w="full">
                  <Show if={today < highlightUntil}>
                    <Image src={getAsset('sparkle.svg')} />
                  </Show>
                  <Text textStyle="captionSmallRegular" noOfLines={1} maxW="40">
                    {request?.account?.name}
                  </Text>

                  <Show if={!!request?.classifications?.length}>
                    <Spacer />
                    <Text
                      px="1"
                      borderRadius="2"
                      textStyle="captionSmallRegular"
                      noOfLines={1}
                      bg={request?.color ? `${request.color}.100` : 'unset'}
                      fontWeight="bold"
                      color={request?.color ? `${request.color}.700` : 'unset'}
                    >
                      {/* We only select the first due to it being the one of highest priority */}
                      {/* This lets you have two loose filters without them both being displayed */}
                      {request.classifications?.[0]?.name}
                    </Text>
                  </Show>
                </Stack>
              </Skeleton>
            </Stack>
          </Box>
          <Stack pointerEvents="initial" ml={2}>
            <Tooltip label="Pending, Ready For QC">
              <Badge
                w="30px"
                textAlign="center"
                borderRadius="full"
                color={!pendingQuoteCount ? 'placeholder' : undefined}
                colorScheme={!pendingQuoteCount ? 'unset' : 'red'}
              >
                {pendingQuoteCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Sent To Carrier">
              <Badge
                w="30px"
                textAlign="center"
                borderRadius="full"
                color={!waitingQuoteCount ? 'placeholder' : undefined}
                colorScheme={!waitingQuoteCount ? 'unset' : 'yellow'}
                variant="ghost"
              >
                {waitingQuoteCount}
              </Badge>
            </Tooltip>
            <Tooltip label="Confirm, In Progress">
              <Badge
                w="30px"
                textAlign="center"
                borderRadius="full"
                color={!completedQuoteCount ? 'placeholder' : undefined}
                colorScheme={!completedQuoteCount ? 'unset' : 'green'}
                variant="ghost"
              >
                {completedQuoteCount}
              </Badge>
            </Tooltip>
          </Stack>
        </Stack>
      </ListItem>
    );
  },
);

export const RequestListItem = ({ initialRequest, ...rest }: Props) => {
  const [quoteCounts] = useQuoteCounts(Number(initialRequest?.id));
  const { requestId } = useParams<Params>();
  const { isSelected, shouldHideBorder } = useMemo(() => {
    return {
      isSelected: initialRequest?.id === requestId,
      shouldHideBorder: Math.abs(parseInt(requestId) - parseInt(initialRequest?.id)) === 1,
    };
  }, [initialRequest?.id, requestId]);

  const navigate = useNavigate();

  useHotkeys(
    ['mod+down', 'mod+up'],
    (e) => {
      const switchTo = e.key === 'ArrowUp' ? rest.previousRequestId : rest.nextRequestId;
      if (switchTo) {
        navigate(`requests/${switchTo}/airline`);
        // it ain't pretty but it gets the job done
        document.getElementById(`requestListItem-${switchTo}`).scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    },
    {
      // Fun fact, this scrolls. Never used it before
      preventDefault: true,
      ignoreEventWhen: () => !isSelected,
    },
    [rest.previousRequestId, rest.nextRequestId, isSelected],
  );

  return (
    <RequestListItemBase
      initialRequest={initialRequest}
      quoteCounts={quoteCounts}
      isSelected={isSelected}
      shouldHideBorder={shouldHideBorder}
      {...rest}
    />
  );
};
